import { IconName, LottieAnimationName } from '@billy/design-system'
import React, { ReactElement } from 'react'

import { EInvoicesEmptyActivated, ImportedInvoicesEmptyState, ScoutNotConnectedState } from '../../../assets/images'
import { Scope } from '../../../enums/scope'
import { getImageClassName } from '../../../utils/getClassName'
import { AnimationDimensions, EmptyViewType } from '../types/emptyViewType'

export interface EmptyProps {
  animation?: LottieAnimationName
  animationDimensions?: AnimationDimensions
  illustration?: ReactElement
  title: string
  description: string
  buttonText?: string
  buttonIcon?: IconName
  buttonVisibilityScope?: Scope | Scope[]
}

export const emptyViewVariants: Record<EmptyViewType, EmptyProps> = {
  creditors: {
    animation: 'billsEmptyState',
    title: 'empty_state.creditors.title',
    description: 'empty_state.creditors.description',
    buttonText: 'create_bill',
  },
  debtors: {
    animation: 'invoicesEmptyState',
    title: 'empty_state.debtors.title',
    description: 'empty_state.debtors.description',
    buttonText: 'create_invoice',
  },
  externalInvoices: {
    title: 'empty_state.external_invoices.title',
    description: 'empty_state.external_invoices.description',
    buttonText: 'empty_state.external_invoices.create_first_invoice',
  },
  externalInvoicesAndEInvoices: {
    title: 'empty_state.external_invoices_and_e_invoices.title',
    description: 'empty_state.external_invoices_and_e_invoices.description',
    buttonText: 'empty_state.external_invoices_and_e_invoices.create_first_invoice',
  },
  importedInvoices: {
    // The class puts the SVG on the same dark mode filter list as the `.ds-lottie-animation` it replaced.
    illustration: <ImportedInvoicesEmptyState className={getImageClassName()} />,
    title: 'empty_state.imported_invoices.title',
    description: 'empty_state.imported_invoices.description',
  },
  externalProducts: {
    animation: 'productsEmptyState',
    title: 'empty_state.external_products.title',
    description: 'empty_state.external_products.description',
    buttonText: 'create_product',
  },
  invoices: {
    animation: 'invoicesEmptyState',
    title: 'empty_state.invoices.title',
    description: 'empty_state.invoices.description',
    buttonText: 'create_invoice',
  },
  salesReport: {
    animation: 'invoicesEmptyState',
    title: 'empty_state.sales_overview.title',
    description: 'empty_state.sales_overview.description',
    buttonText: 'create_invoice',
  },
  quotes: {
    animation: 'quotesEmptyState',
    title: 'empty_state.quotes.title',
    description: 'empty_state.quotes.description',
    buttonText: 'create_quote',
  },
  recurringInvoices: {
    animation: 'recurringInvoicesEmptyState',
    title: 'empty_state.recurring_invoices.title',
    description: 'empty_state.recurring_invoices.description',
    buttonText: 'create_recurring_invoice',
  },
  products: {
    animation: 'productsEmptyState',
    title: 'empty_state.products.title',
    description: 'empty_state.products.description',
    buttonText: 'create_products',
  },
  bills: {
    animation: 'billsEmptyState',
    title: 'empty_state.bills.title',
    description: 'empty_state.bills.description',
    buttonText: 'create_bill',
  },
  bank: {
    animation: 'bankEmptyState',
    title: 'empty_state.bank.title',
    description: 'empty_state.bank.description',
  },
  bankConnectionEstablished: {
    animation: 'bankEmptyState',
    title: 'empty_state.bankConnectionEstablished.title',
    description: 'empty_state.bankConnectionEstablished.description',
  },
  bankNotConnected: {
    animation: 'bankEmptyState',
    title: 'empty_state.bank_not_connected.title',
    description: 'empty_state.bank_not_connected.description',
  },
  bankNotConnectedUnauthorized: {
    animation: 'bankEmptyState',
    title: 'empty_state.import_transactions.title',
    description: 'empty_state.import_transactions.description',
    buttonText: 'empty_state.import_transactions.buttonText',
    buttonIcon: 'uploadCloud',
  },
  contacts: {
    animation: 'contactsEmptyState',
    title: 'empty_state.contacts.title',
    description: 'empty_state.contacts.description',
    buttonText: 'create_contact',
  },
  transactions: {
    animation: 'transactionsEmptyState',
    title: 'empty_state.transactions.title',
    description: 'empty_state.transactions.description',
    buttonText: 'empty_state.transactions.create_transaction',
  },
  einvoicesRegistered: {
    animation: 'invoicesEmptyState',
    title: 'empty_state.einvoice.registered.title',
    description: 'empty_state.einvoice.registered.description',
    buttonText: 'empty_state.einvoice.buttonText',
  },
  einvoicesNotRegistered: {
    animation: 'invoicesEmptyState',
    title: 'empty_state.einvoice.not_registered.title',
    description: 'empty_state.einvoice.not_registered.description',
    buttonText: 'empty_state.einvoice.buttonText',
  },
  einvoicesBlocked: {
    illustration: <EInvoicesEmptyActivated />,
    title: 'empty_state.einvoice.blocked.title',
    description: 'empty_state.einvoice.blocked.description',
  },
  einvoicesApproved: {
    illustration: <EInvoicesEmptyActivated />,
    title: 'empty_state.einvoice.approved.title',
    description: 'empty_state.einvoice.approved.description',
  },
  scoutConnected: {
    animation: 'invoicesEmptyState',
    title: 'empty_state.scout.connected.title',
    description: 'empty_state.scout.connected.description',
  },
  scoutConnectedFilePicker: {
    animation: 'invoicesEmptyState',
    title: 'empty_state.scout.connected.title',
    description: 'empty_state.file_picker.scout.connected.description',
  },
  scoutConnectedProcessing: {
    animation: 'scoutConnectedProcessing',
    title: 'empty_state.scout.connected_processing.title',
    description: 'empty_state.scout.connected_processing.description',
  },
  scoutNotConnected: {
    illustration: <ScoutNotConnectedState />,
    title: 'empty_state.scout.not_connected.title',
    description: 'empty_state.scout.not_connected.description',
    buttonText: 'empty_state.scout.buttonText',
  },
  vatDeclarations: {
    animation: 'billsEmptyState',
    title: 'empty_state.vat_declarations.title',
    description: 'empty_state.vat_declarations.description',
  },
  documents: {
    animation: 'billsEmptyState',
    title: 'empty_state.documents.title',
    description: 'empty_state.documents.description',
  },
}
